import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';

import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Charge } from '../../models/entities/charge';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { RecordOfflinePayment } from '../../components/app-layout/payments/record-owner-payment/record-offline-payment.model';
import { PaymentGroup } from '../../models/entities/payment-group';
import { AccountCredit } from 'app/models/entities/account-credit';
import { PaymentsResponse } from './payments.datasource';
import { Moment } from 'moment-timezone';
import { PaymentGroups } from 'app/components/shared/http/request-interfaces';

export interface PaginatedPaymentHistory {
    data: PaymentGroup[];
    meta: {
        currentPage: number;
        lastPage: number;
        perPage: number;
        total: number;
        from: number | null;
        to: number | null;
    };
}

@Injectable({
    providedIn: 'root',
})
export class PaymentGroupsService
{
    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * Gets specific payments received by organization from Core.
     * @param {number} organizationId
     * @param {number} paymentId
     * @returns {Observable<PaymentGroup>}
     */
    public getIncomingPaymentGroup(
            organizationId: number,
            paymentId: number,
    ): Observable<PaymentGroup> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.IncomingPaymentGroup(
                        organizationId || jwt.orgId,
                        paymentId,
                ),
        );

        return this._http.get(url).pipe(map((response: Object) => {
            return new PaymentGroup(response);
        }));
    }

    /**
     * @param organizationId
     * @param paymentId
     */
    public issueCreditForPaymentDifference(
            organizationId: number,
            paymentId: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.IncomingPaymentGroup(
                        organizationId || jwt.orgId,
                        paymentId,
                ) + '/issue-credit/missing',
        );

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Gets filters used for incoming payments
     * @param organizationId
     */
    public getPaymentFilters(organizationId: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.IncomingPaymentsFilters(organizationId || jwt.orgId),
        );

        return this._http.get(url);
    }

    /**
     * Gets filters used for incoming payments
     */
    public getAdminPaymentFilters() {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminPaymentFilters(),
        );

        return this._http.get(url);
    }

    /**
     * Gets specific payments received by organization from Core.
     * @param orgId
     * @returns {Observable<PaymentsResponse>}
     */
    public getIncomingPaymentGroups(
            orgId: number,
            request: PaymentGroups.GetIncomingPaymentGroupsRequest,
    ): Observable<PaymentsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const params = [];
        params.push('page=' + request.page);
        params.push('search=' + encodeURIComponent(request.search));
        params.push('column=' + request.column);
        params.push('direction=' + request.direction);
        params.push('perPage=' + request.perPage);
        params.push('filters=' + JSON.stringify(request.filters));

        let url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.IncomingPaymentGroups(orgId || jwt.orgId));
        url = url + '?' + params.join('&');

        return this._http.get(url).pipe(map((resp) => {
            return new PaymentsResponse(resp);
        }));
    }

    /**
     * Dashboard endpoint — same data as getIncomingPaymentGroups but with enhanced meta:
     * per-status totals, method/source breakdowns, monthly chart data.
     */
    public getIncomingPaymentGroupsDashboard(
            orgId: number,
            request: PaymentGroups.GetIncomingPaymentGroupsRequest,
    ): Observable<PaymentsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const params = [];
        params.push('page=' + request.page);
        params.push('search=' + encodeURIComponent(request.search));
        params.push('column=' + request.column);
        params.push('direction=' + request.direction);
        params.push('perPage=' + request.perPage);
        params.push('filters=' + JSON.stringify(request.filters));

        let url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.IncomingPaymentGroupsDashboard(orgId || jwt.orgId));
        url = url + '?' + params.join('&');

        return this._http.get(url).pipe(map((resp) => {
            return new PaymentsResponse(resp);
        }));
    }

    /**
     * Gets specific payments received by organization from Core.
     * @param orgId
     * @returns {Observable<PaymentsResponse>}
     */
    public getIncomingPaymentGroupsCsv(
            orgId: number,
            request: PaymentGroups.GetIncomingPaymentGroupsRequest,
    ): Observable<PaymentsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const params = [];
        params.push('page=' + request.page);
        params.push('search=' + encodeURIComponent(request.search));
        params.push('column=' + request.column);
        params.push('direction=' + request.direction);
        params.push('perPage=' + request.perPage);
        params.push('filters=' + JSON.stringify(request.filters));

        let url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.IncomingPaymentGroups(orgId || jwt.orgId));
        url = url + '?' + params.join('&');

        return this._http.get(url, {responseType: 'blob'});
    }

    /**
     *
     * @param orgId
     * @param search
     * @param sortColumn
     * @param sortDirection
     * @param pageIndex
     * @param perPage
     * @param options
     */
    public getAdminPaymentGroups(
            search: string = '',
            sortColumn: string = 'createdAt',
            sortDirection: string = 'desc',
            pageIndex: number = 1,
            perPage: number = 100,
            options: any = {},
    ): Observable<PaymentsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const params = [];
        params.push('page=' + pageIndex);
        params.push('search=' + encodeURIComponent(search));
        params.push('column=' + sortColumn);
        params.push('direction=' + sortDirection);
        params.push('perPage=' + perPage);
        if (typeof options !== 'undefined') {
            params.push('filters=' + JSON.stringify(options));
        }

        let url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AdminPaymentGroups());
        url = url + '?' + params.join('&');

        if (options.csv) {
            return this._http.get(url, {responseType: 'blob'});
        } else {
            return this._http.get(url).pipe(map((resp) => {
                return new PaymentsResponse(resp);
            }));
        }
    }

    /**
     * Get payment history with pagination support
     * @param payorId - Organization ID (defaults to JWT orgId)
     * @param parentId - Parent organization ID (for org2org)
     * @param year - Year filter (-1 or null for all time)
     * @param page - Page number
     * @param perPage - Results per page
     * @param search - Search string to filter results
     */
    public getPaymentHistory(
            payorId?: number,
            parentId?: number,
            year?: number,
            page: number = 1,
            perPage: number = 25,
            search?: string,
    ): Observable<PaginatedPaymentHistory> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.IncomingPaymentGroups(payorId || jwt.orgId) +
                '/history',
        );

        const params: string[] = [];
        params.push('page=' + page);
        params.push('perPage=' + perPage);

        if (parentId) {
            params.push('parent=' + parentId);
        }
        if (year !== undefined && year !== null) {
            params.push('year=' + year);
        }
        if (search) {
            params.push('search=' + encodeURIComponent(search));
        }

        if (params.length > 0) {
            url = url + '?' + params.join('&');
        }

        return this._http.get(url).pipe(map((res: any) => {
            return {
                data: res.data.map((pg: any) => new PaymentGroup(pg)),
                meta: res.meta,
            };
        }));
    }

    /**
     * Gets payments made by payor from Core.
     * @param {number} organizationId
     * @param memberId
     * @param {number[]} paymentGroupIds optional
     * @returns {Observable<PaymentGroup[]>}
     */
    public getMemberPaymentGroups(
            organizationId: number,
            memberId: number,
            paymentGroupIds?: number[],
    ): Observable<PaymentGroup[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const params =
                paymentGroupIds && paymentGroupIds.length
                        ? '?paymentGroupIds=' + paymentGroupIds.join(',')
                        : '';
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.MemberPaymentGroups(
                        organizationId,
                        memberId,
                ) + params,
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(paymentGroup => {
                return new PaymentGroup(paymentGroup);
            });
        }));
    }

    /**
     * Gets account credits on this unit from all sources
     * @param {number} organizationId
     * @param {number} unitId
     * @param {number[]} paymentGroupIds optional
     * @returns {Observable<AccountCredit[]>}
     */
    public getUnitAccountCredits(
            organizationId: number,
            unitId: number,
    ): Observable<AccountCredit[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationUnit(
                        organizationId || jwt.orgId,
                        unitId,
                ) +
                '/account-credits',
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(paymentGroup => {
                return new AccountCredit(paymentGroup);
            });
        }));
    }

    /**
     * Delete account credit
     */
    public deleteUnitAccountCredit(
            organizationId: number,
            unitId: number,
            creditId: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationUnit(
                        organizationId || jwt.orgId,
                        unitId,
                ) +
                '/account-credits/' + creditId,
        );

        return this._http.delete(url);
    }

    /**
     * Restore account credit
     */
    public restoreUnitAccountCredit(
            organizationId: number,
            unitId: number,
            creditId: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationUnit(
                        organizationId || jwt.orgId,
                        unitId,
                ) +
                '/account-credits/' + creditId + '/restore',
        );

        return this._http.post(url, JSON.stringify({}));
    }

    public processAccountCreditAsItCameIn(
            organizationId: number,
            unitId: number,
            creditId: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationUnit(
                        organizationId || jwt.orgId,
                        unitId,
                ) +
                '/account-credits/' + creditId + '/process',
        );

        return this._http.post(url, JSON.stringify({}));
    }

    public processUnitCreditPayment(
            organizationId: number,
            unitId: number,
            chargeId: number = null,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationUnit(
                        organizationId || jwt.orgId,
                        unitId,
                ) + '/account-credits/process',
        );

        if (chargeId) {
            url += '?charge=' + chargeId;
        }

        return this._http.post(url, JSON.stringify({}));
    }

    public updatePaymentGroupTotals(organizationId: number, paymentGroupId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.UpdatePaymentGroupTotals(
                        organizationId || jwt.orgId,
                        paymentGroupId,
                ),
        );

        return this._http.post(url, JSON.stringify({}));
    }

    public recordLockboxPaymentGroups(
            organizationId: number,
            paymentRecords: {
                amount: number,
                payorId: number,
                note: string,
                paymentDate: string
            }[],
            sendReceipts: boolean, addToLedger: boolean, depositAccount: null,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LockboxPaymentGroups(organizationId || jwt.orgId),
        );

        const request = {payments: paymentRecords, sendReceipts: sendReceipts};
        if (addToLedger) {
            request['addToLedger'] = true;
            request['depositAccount'] = depositAccount;
        }
        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Record payment records
     * @param {number} organizationId
     * @param {RecordOfflinePayment[]} paymentRecords optional
     * @param addToLedger
     * @param selectedAccountId
     * @param depositDate
     * @returns {Observable<PaymentGroup[]>}
     */
    public recordOfflinePaymentGroups(
            organizationId: number,
            paymentRecords: RecordOfflinePayment[],
            addToLedger: boolean = false,
            selectedAccountId: number = null,
            depositDate: Moment = null,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OfflinePaymentGroups(organizationId || jwt.orgId),
        );

        const payments: {
            amount: number;
            payorId: number;
            payorType: string;
            note: string;
            paymentMethodType: string;
            charges: { chargeId: number; paymentAmount: number }[];
            sendReceipt: boolean;
            paymentDate: string;
        }[] = [];

        for (const record of paymentRecords) {
            payments.push({
                amount: record.amount,
                payorId: record.payor.id,
                payorType: 'owner',
                note: record.note,
                paymentMethodType: record.paymentMethodType,
                sendReceipt: record.sendReceipt,
                paymentDate: record.paymentDate.format('YYYY-MM-DD'),
                charges: record.charges
                        .filter((charge: Charge) => {
                            return charge.paymentAmount > 0;
                        })
                        .map((charge: Charge) => {
                            return {
                                chargeId: charge.id,
                                paymentAmount: charge.paymentAmount,
                            };
                        }),
            });
        }

        const requestData = {payments: payments};
        if (addToLedger) {
            requestData['addToLedger'] = true;
            requestData['accountId'] = selectedAccountId;
            requestData['depositDate'] = depositDate.format('YYYY-MM-DD');
        }

        return this._http.post(url, JSON.stringify(requestData));
    }

    public linkOfflinePaymentGroups(paymentsToLink: number[], depositToLinkId: number): Observable<{ batchId: string }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LinkPaymentGroups(jwt.orgId),
        );

        const requestData = {
            paymentsToLink: paymentsToLink,
            depositToLinkId: depositToLinkId,
        };

        return this._http.post(url, JSON.stringify(requestData)) as Observable<{ batchId: string }>;
    }

    public unlinkOfflinePaymentGroups(orgId: number, paymentsToUnlink: number[]): Observable<{ batchId: string }> {
        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnlinkPaymentGroups(orgId));

        const requestData = {
            paymentsToUnlink: paymentsToUnlink,
        };

        return this._http.post(url, JSON.stringify(requestData)) as Observable<{ batchId: string }>;
    }

    /**
     * Issue a credit for a given payment item
     * Must be called by a super
     */
    public issueCreditForPaymentItem(paymentItemId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.IssueCreditForPaymentItem(paymentItemId),
        );

        return this._http.post(url, JSON.stringify({}));
    }

    public deletePaymentGroups(
            organizationId: number,
            paymentIds: number[],
            reason: string,
    ): Observable<Object> {
        if (paymentIds.length === 0) {
            return;
        }
        const base = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.DeletePaymentGroups(organizationId, paymentIds, reason));
        return this._http.delete(base);
    }

    public deletePaymentGroup(
            organizationId: number,
            paymentId: number,
            reason: string,
    ): Observable<Object> {
        const base = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.DeletePaymentGroup(organizationId, paymentId, reason));
        return this._http.delete(base);
    }

    /**
     * @param {number} organizationId
     * @param paymentId
     * @param payload
     * @returns {Observable<PaymentGroup>}
     */
    public editPaymentGroup(
            organizationId: number,
            paymentId: number,
            payload: {
                createdAt: Moment,
                memo: string,
            },
    ): Observable<PaymentGroup> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.IncomingPaymentGroups(organizationId || jwt.orgId)) +
                '/' + paymentId;

        return this._http.patch(url, JSON.stringify({
            createdAt: payload.createdAt.format('YYYY-MM-DD'),
            memo: payload.memo,
        })).pipe(map((response: Object) => {
            return new PaymentGroup(response);
        }));
    }

    public returnPayment(
            orgId: number,
            paymentId: number,
            returnFees: boolean,
            bankAccount: number,
            reason: string,
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string =
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.ReturnPaymentGroup(
                                orgId || jwt.orgId,
                                paymentId,
                        ),
                );

        return this._http.post(url, JSON.stringify({
            reason: reason,
            bankAccount: bankAccount,
            returnFees: returnFees,
        }));
    }

    public returnPayments(
            orgId: number,
            paymentIds: number[],
            returnFees: boolean,
            bankAccount: number,
            reason: string,
            returnDate: Moment,
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ReturnPaymentGroups(orgId || jwt.orgId));

        return this._http.post(url, JSON.stringify({
            payments: paymentIds,
            reason: reason,
            bankAccount: bankAccount,
            returnProcessingFees: returnFees,
            returnDate: returnDate.format('YYYY-MM-DD'),
        }));
    }

    public sendReceiptsForPaymentGroup(paymentGroupIds: number[]): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.IncomingPaymentsReceipts(jwt.orgId));

        return this._http.post(url, JSON.stringify({
            paymentGroupIds: paymentGroupIds,
        }));
    }

    public downloadReceipt(organizationId: number, paymentGroupId: number): Observable<Blob> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.PaymentGroupReceipt(organizationId || jwt.orgId, paymentGroupId),
        );

        return this._http.get(url, { responseType: 'blob' });
    }
}
